<script setup lang="ts">
import { computed } from 'vue'

import { formatBytes, formatPercent } from '@/lib/format'
import type { Stage } from '@/types'

const props = withDefaults(
  defineProps<{
    stage: Stage
    percent: number
    downloadedBytes?: number
    totalBytes?: number
    queuePosition?: number
    reconnecting?: boolean
  }>(),
  { downloadedBytes: 0, totalBytes: 0, queuePosition: 0, reconnecting: false },
)

const STEPS: readonly Stage[] = ['queued', 'downloading', 'converting']

/**
 * The rail under the bar. `Stage` is a wire value, so it never reaches the
 * screen untranslated — this is the only place it is spelled for a person.
 */
const STEP_NAMES: Record<Stage, string> = {
  queued: 'fila',
  downloading: 'baixando',
  converting: 'convertendo',
}

/** Only the download stage reports real bytes; the encoder reports nothing. */
const indeterminate = computed(() => props.stage !== 'downloading')

const clamped = computed(() => Math.max(0, Math.min(100, props.percent || 0)))

const label = computed(() => {
  if (props.reconnecting) return 'Reconectando'
  switch (props.stage) {
    case 'queued':
      return props.queuePosition > 0 ? 'Na fila' : 'Iniciando'
    case 'downloading':
      return 'Baixando áudio'
    case 'converting':
      return 'Convertendo'
  }
})

const readout = computed(() => {
  if (props.stage === 'queued') {
    return props.queuePosition > 0 ? `${props.queuePosition}º na fila` : '—'
  }
  if (props.stage === 'downloading') {
    if (props.totalBytes > 0) {
      return `${formatBytes(props.downloadedBytes)} / ${formatBytes(props.totalBytes)} · ${formatPercent(clamped.value)}`
    }
    return formatPercent(clamped.value)
  }
  return '192 kbps'
})

const ariaText = computed(() =>
  indeterminate.value
    ? `${label.value}, progresso desconhecido`
    : `${label.value}, ${formatPercent(clamped.value)}`,
)

const activeIndex = computed(() => STEPS.indexOf(props.stage))
</script>

<template>
  <div class="prog">
    <div class="prog__head">
      <span class="prog__label">{{ label }}</span>
      <span class="prog__readout mono">{{ readout }}</span>
    </div>

    <div
      class="prog__track"
      role="progressbar"
      :aria-label="label"
      :aria-valuemin="indeterminate ? undefined : 0"
      :aria-valuemax="indeterminate ? undefined : 100"
      :aria-valuenow="indeterminate ? undefined : Math.round(clamped)"
      :aria-valuetext="ariaText"
    >
      <div
        v-if="indeterminate"
        class="prog__fill prog__fill--indeterminate"
        :class="{ 'prog__fill--stalled': props.reconnecting }"
      />
      <div v-else class="prog__fill" :style="{ width: `${clamped}%` }" />
    </div>

    <ol class="prog__steps">
      <li
        v-for="(step, index) in STEPS"
        :key="step"
        class="prog__step"
        :class="{
          'is-done': index < activeIndex,
          'is-active': index === activeIndex,
        }"
      >
        <span class="prog__step-dot" aria-hidden="true" />
        <span class="prog__step-name">{{ STEP_NAMES[step] }}</span>
      </li>
    </ol>
  </div>
</template>

<style scoped>
.prog {
  display: grid;
  gap: 0.625rem;
  width: 100%;
}

.prog__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}

.prog__label {
  font-weight: 600;
  font-size: var(--step--1);
  letter-spacing: -0.01em;
}

.prog__readout {
  color: var(--text-2);
  font-size: var(--step--2);
  white-space: nowrap;
}

.prog__track {
  position: relative;
  height: 6px;
  border-radius: var(--radius-pill);
  background: var(--bg-3);
  overflow: hidden;
}

.prog__fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, var(--accent), #eaff9b);
  box-shadow: 0 0 14px var(--accent-glow);
  transition: width 320ms var(--ease-out);
}

.prog__fill--indeterminate {
  width: 42%;
  animation: slide 1.25s var(--ease) infinite;
}

.prog__fill--stalled {
  background: linear-gradient(90deg, var(--text-3), var(--text-2));
  box-shadow: none;
}

@keyframes slide {
  0% {
    transform: translateX(-105%);
  }

  100% {
    transform: translateX(240%);
  }
}

.prog__steps {
  display: flex;
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.prog__step {
  display: flex;
  align-items: center;
  gap: 0.4375rem;
  font-family: var(--font-mono);
  font-size: var(--step--2);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-3);
  transition: color var(--dur) var(--ease);
}

.prog__step-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--line-2);
  transition: background-color var(--dur) var(--ease);
}

.prog__step.is-done {
  color: var(--text-2);
}

.prog__step.is-done .prog__step-dot {
  background: var(--text-2);
}

.prog__step.is-active {
  color: var(--accent);
}

.prog__step.is-active .prog__step-dot {
  background: var(--accent);
  animation: pulse-dot 1.4s ease-in-out infinite;
}

@media (max-width: 24rem) {
  .prog__steps {
    gap: 0.75rem;
  }

  .prog__step-name {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .prog__fill--indeterminate {
    width: 100%;
    animation: none;
    opacity: 0.5;
  }

  .prog__step.is-active .prog__step-dot {
    animation: none;
  }
}
</style>
